import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, Search, Truck, Clock, MapPin, X, Loader2, AlertCircle, Send, ArrowRightLeft } from 'lucide-react';
import { api } from '../lib/api';

export default function LabelsArchive({ onViewLabel }) {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoadingFor, setPdfLoadingFor] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // '' = both directions; otherwise OUTBOUND or RETURN.
  const [directionFilter, setDirectionFilter] = useState('');

  const [trackingModal, setTrackingModal] = useState({
    isOpen: false,
    barcode: null,
    loading: false,
    scans: [],
    error: null,
  });

  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getLabels({ search, direction: directionFilter });
      setLabels(data.labels || []);
    } catch (err) {
      setError(err.message);
      setLabels([]);
    } finally {
      setLoading(false);
    }
  }, [search, directionFilter]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * PDFs are not included in the listing -- each is hundreds of kilobytes of
   * base64 -- so fetch on click, falling back to HP if we never stored one.
   */
  const handleViewPdf = async (label) => {
    setPdfLoadingFor(label.barcode);
    try {
      const { pdfBase64 } = await api.getLabelPdf(label.barcode);
      onViewLabel(
        {
          barcode: label.barcode,
          clientReferenceNumber: label.clientReferenceNumber,
          direction: label.direction,
          labelPdfBase64: pdfBase64,
        },
        label.orderName
      );
    } catch (err) {
      setError(`Could not load the PDF for ${label.barcode}: ${err.message}`);
    } finally {
      setPdfLoadingFor(null);
    }
  };

  const handleOpenTracking = async (barcode) => {
    setTrackingModal({ isOpen: true, barcode, loading: true, scans: [], error: null });
    try {
      const data = await api.getLabelStatus(barcode);
      setTrackingModal({ isOpen: true, barcode, loading: false, scans: data.scans || [], error: null });
    } catch (err) {
      setTrackingModal({ isOpen: true, barcode, loading: false, scans: [], error: err.message });
    }
  };

  return (
    <div className="polaris-card">
      <div className="card-header">
        <div>
          <h2 className="card-title">HP labels archive ({labels.length})</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', marginTop: '0.15rem' }}>
            Every shipping and return label created for this store, with live Hrvatska Pošta tracking.
          </p>
        </div>
      </div>

      <div className="toolbar-row" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', background: '#fafafa' }}>
        <div className="toolbar-search">
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-secondary)',
            }}
          />
          <input
            type="text"
            className="form-control"
            style={{ paddingLeft: '2.3rem' }}
            placeholder="Search by barcode, order number or customer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="toolbar-actions">
          {[
            ['', 'All'],
            ['OUTBOUND', 'Shipping'],
            ['RETURN', 'Returns'],
          ].map(([value, label]) => (
            <button
              key={value || 'all'}
              className={`btn btn-sm ${directionFilter === value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDirectionFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          style={{
            margin: '1rem 1.5rem 0',
            background: '#fce8e6',
            color: '#c5221f',
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="table-responsive" role="region" aria-label="Generated labels" tabIndex={0}>
        <table className="polaris-table">
          <thead>
            <tr>
              <th>Barcode</th>
              <th>Direction</th>
              <th>Order</th>
              <th>Customer</th>
              <th>Service</th>
              <th>Route</th>
              <th>Latest HP status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="table-state">
                  <div className="table-state-inner">
                    <Loader2 size={28} className="spin" />
                  </div>
                </td>
              </tr>
            ) : labels.length > 0 ? (
              labels.map((lbl) => (
                <tr key={lbl.id}>
                  <td data-label="Barcode" className="table-primary-cell">
                    <strong style={{ color: 'var(--color-brand-hp)', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                      {lbl.barcode}
                    </strong>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-secondary)' }}>
                      Ref: {lbl.clientReferenceNumber}
                    </div>
                  </td>
                  <td data-label="Direction">
                    {lbl.direction === 'OUTBOUND' ? (
                      <span className="badge badge-green">
                        <Send size={13} /> Shipping
                      </span>
                    ) : (
                      <span className="badge badge-yellow">
                        <ArrowRightLeft size={13} /> Return
                      </span>
                    )}
                  </td>
                  <td data-label="Order">
                    <strong>{lbl.orderName}</strong>
                  </td>
                  <td data-label="Customer">{lbl.customerName}</td>
                  <td data-label="Service">
                    <span className="badge badge-purple">{lbl.serviceName}</span>
                    {lbl.codAmount != null && (
                      <div style={{ fontSize: '0.72rem', color: '#6b4e00', marginTop: '0.2rem', fontWeight: 600 }}>
                        COD{' '}
                        {new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(
                          lbl.codAmount
                        )}
                      </div>
                    )}
                  </td>
                  <td data-label="Route">
                    <span className="badge badge-blue">{lbl.pickupTypeName}</span>
                    {/* Only outbound labels carry a delivery choice; a return
                        always travels to the merchant's own address. */}
                    {lbl.deliveryTypeName && (
                      <div style={{ marginTop: '0.2rem' }}>
                        <span className="badge badge-gray">{lbl.deliveryTypeName}</span>
                      </div>
                    )}
                  </td>
                  <td data-label="Latest HP status" style={{ fontSize: '0.8rem' }}>
                    {lbl.status}
                  </td>
                  <td data-label="Created" style={{ fontSize: '0.85rem' }}>
                    {new Date(lbl.createdAt).toLocaleString('hr-HR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="table-actions" style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleOpenTracking(lbl.barcode)}
                        title="Track this parcel at Hrvatska Pošta"
                      >
                        <Truck size={14} /> Track
                      </button>
                      <button
                        className="btn btn-hp btn-sm"
                        onClick={() => handleViewPdf(lbl)}
                        disabled={pdfLoadingFor === lbl.barcode}
                      >
                        {pdfLoadingFor === lbl.barcode ? (
                          <Loader2 size={14} className="spin" />
                        ) : (
                          <Eye size={14} />
                        )}{' '}
                        Label PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="table-state">
                  <div className="table-state-inner">No return labels created yet.</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {trackingModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: '620px' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ background: '#e4f5ff', color: '#0068a5', padding: '0.4rem', borderRadius: '6px', display: 'flex' }}>
                  <Truck size={18} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>HP tracking — {trackingModal.barcode}</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)' }}>
                    Scan history from Hrvatska Pošta
                  </p>
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setTrackingModal({ isOpen: false, scans: [] })}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {trackingModal.loading ? (
                <div style={{ textAlign: 'center', padding: '2.5rem' }}>
                  <Loader2 size={32} className="spin" style={{ color: 'var(--color-brand-hp)', marginBottom: '0.5rem' }} />
                  <p style={{ fontSize: '0.9rem', color: 'var(--color-secondary)' }}>
                    Fetching the scan timeline from Hrvatska Pošta...
                  </p>
                </div>
              ) : trackingModal.error ? (
                <div style={{ color: '#c5221f', background: '#fce8e6', padding: '1rem', borderRadius: '6px' }}>
                  {trackingModal.error}
                </div>
              ) : trackingModal.scans.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-secondary)' }}>
                  <Clock size={28} style={{ opacity: 0.4, marginBottom: '0.5rem' }} />
                  <p>Hrvatska Pošta has the shipment order but has not scanned the parcel yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'relative', paddingLeft: '1rem' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: '1.75rem',
                      top: '10px',
                      bottom: '10px',
                      width: '2px',
                      background: '#e1e3e5',
                      zIndex: 0,
                    }}
                  />
                  {trackingModal.scans.map((scan, idx) => (
                    <div
                      key={`${scan.Scan}-${scan.ScanTime}-${idx}`}
                      style={{ display: 'flex', gap: '1.25rem', position: 'relative', zIndex: 1, alignItems: 'flex-start' }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background:
                            idx === trackingModal.scans.length - 1
                              ? 'var(--color-shopify-green)'
                              : 'var(--color-brand-hp)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          flexShrink: 0,
                          boxShadow: '0 0 0 4px white',
                        }}
                      >
                        {scan.Scan}
                      </div>

                      <div
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          padding: '0.85rem 1rem',
                          borderRadius: '8px',
                          flex: 1,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-primary)' }}>
                          {scan.ScanDescription}
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--color-secondary)',
                            marginTop: '0.2rem',
                            display: 'flex',
                            gap: '1rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span>
                            <Clock size={12} style={{ display: 'inline', marginRight: '3px' }} />{' '}
                            {new Date(scan.ScanTime).toLocaleString('hr-HR')}
                          </span>
                          {scan.Center && (
                            <span>
                              <MapPin size={12} style={{ display: 'inline', marginRight: '3px' }} /> {scan.Center}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTrackingModal({ isOpen: false, scans: [] })}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
